// Layout · KVGrid
//
// Flat key/value grid — uppercase eyebrow labels, primary-text values.
// Composes `Grid` for the outer layout + `TextValue` for typography.
//
// Affordance contract:
//   - When `item.editable` (or `item.locked`) is set, KVGrid renders the
//     single edit/lock icon next to the LABEL. Hovering anywhere on the cell
//     escalates the icon opacity (35% → 90%).
//   - KVGrid provides `LabelAffordanceContext = true` to the value subtree
//     so a nested `<Editable>` suppresses its own value-side pencil but
//     keeps its hover background + click behaviour. ONE icon, predictable
//     position.

import React, { useState } from "react";
import { EditOutlined, LockOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import {
  FONT_SIZE,
  MOTION,
  SKELETON,
  TEXT,
  type ToneName,
} from "@ds/tokens/palette";
import { Skeleton as SkeletonBlock } from "@ds/display/Skeleton";
import { EmptyValue } from "@ds/display/EmptyValue";
import { TextValue, type TextSize, type TextTone } from "@ds/display/TextValue";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Grid } from "@ds/layout/Grid";
import { Box } from "@ds/layout/Box";
import { LabelAffordanceContext } from "@ds/editable/affordanceContext";

function toLabelSlug(label: React.ReactNode, idx: number): string {
  if (typeof label === "string") {
    const slug = label
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
    return `kv-${slug || idx}`;
  }
  return `kv-${idx}`;
}

function toValueTestId(label: React.ReactNode, idx: number): string {
  if (typeof label === "string") {
    const slug = label
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
    return `kv-${slug || idx}-value`;
  }
  return `kv-${idx}-value`;
}

const VALUE_TEXT_SIZE: Record<"sm" | "md" | "lg", TextSize> = {
  sm: "sm",
  md: "md",
  lg: "lg",
};

export interface KVItem {
  label: React.ReactNode;
  value: React.ReactNode;
  hidden?: boolean;
  strong?: boolean;
  fullWidth?: boolean;
  editable?: boolean;
  locked?: boolean | { reason: string };
  sub?: React.ReactNode;
  tone?: ToneName;
  size?: "sm" | "md" | "lg";
  renderValue?: () => React.ReactNode;
}

export interface KVGridInput {
  items: KVItem[];
  /**
   * How the pairs are printed. Same items, same behaviour (edit / lock /
   * loading / tone / sub) in every mode — only the typography and tracks differ.
   *
   * - `"grid"`   — stacked label-over-value cells in an N-column grid.
   * - `"rows"`   — the same cells in one column.
   * - `"ledger"` — label and value on ONE line in their own track. Label is
   *   sentence-case muted, value bold. `ledgerAlign` decides which edge they
   *   hang from.
   */
  layout?: "grid" | "rows" | "ledger";
  /**
   * `"right"` (default) flushes the pair to the right edge — an invoice's dates
   * and payment terms. `"left"` hangs it from the left with the value column
   * filling — a dense field list. Only meaningful when `layout="ledger"`.
   */
  ledgerAlign?: "left" | "right";
  columns?: number;
  compact?: boolean;
  loading?: boolean;
  testId?: string;
}

export type KVGridProps = KVGridInput;

/** The per-item facts every layout needs — derived once, printed differently. */
function useKVItemState(item: KVItem) {
  const lockMeta   = typeof item.locked === "object" ? item.locked : undefined;
  const isLocked   = !!item.locked;
  const isEditable = !!item.editable && !isLocked;

  const isEmpty = !item.renderValue && (item.value == null || item.value === "");
  const renderedValue = item.renderValue
    ? item.renderValue()
    : isEmpty
      ? <EmptyValue />
      : item.value;

  const effectiveSize: "sm" | "md" | "lg" = item.strong ? "lg" : (item.size ?? "md");

  return {
    lockMeta,
    isLocked,
    isEditable,
    renderedValue,
    valueTextSize: VALUE_TEXT_SIZE[effectiveSize],
    valueTone: (item.tone ?? "primary") as TextTone,
  };
}

/** The lock/edit affordance pair — identical in every layout, so it lives once. */
function KVAffordances({
  isLocked, isEditable, hovered,
}: { isLocked: boolean; isEditable: boolean; hovered: boolean }) {
  return (
    <>
      {/* AntD icons keep their own `style` prop (AntD pattern). */}
      {isLocked && (
        <LockOutlined style={{ fontSize: FONT_SIZE.micro, color: TEXT.faint }} />
      )}
      {isEditable && (
        <EditOutlined
          style={{
            fontSize:   FONT_SIZE.micro,
            color:      TEXT.muted,
            opacity:    hovered ? 0.9 : 0.35,
            transition: `opacity ${MOTION.fast} ease`,
          }}
        />
      )}
    </>
  );
}

/** Wrap a label in its lock-reason tooltip when there is one to explain. */
function withLockReason(
  node: React.ReactNode,
  isLocked: boolean,
  reason: string | undefined,
): React.ReactNode {
  if (!isLocked || !reason) return node;
  return (
    <Tooltip title={reason} placement="top">
      <Inline as="span" inline>{node}</Inline>
    </Tooltip>
  );
}

interface KVCellProps {
  item: KVItem;
  idx: number;
  loading: boolean;
  layout: "grid" | "rows";
}

function KVCell({ item, idx, loading, layout }: KVCellProps) {
  const [hovered, setHovered] = useState(false);
  const {
    lockMeta, isLocked, isEditable, renderedValue, valueTextSize, valueTone,
  } = useKVItemState(item);

  const labelNode = withLockReason(
    <Inline as="span" inline gap="xs" align="center">
      <KVAffordances isLocked={isLocked} isEditable={isEditable} hovered={hovered} />
      <TextValue
        as="span"
        size="xs"
        weight="semibold"
        tone="muted"
        uppercase
      >
        {item.label}
      </TextValue>
    </Inline>,
    isLocked,
    lockMeta?.reason,
  );

  const valueNode = (
    <>
      <TextValue
        as="div"
        size={valueTextSize}
        weight="semibold"
        tone={valueTone}
        lineHeight="normal"
        wrapWords
        testId={toValueTestId(item.label, idx)}
      >
        {renderedValue}
      </TextValue>
      {item.sub != null && (
        <TextValue as="div" size="xs" tone="muted" lineHeight="relaxed">
          {item.sub}
        </TextValue>
      )}
    </>
  );

  const cellContent = (
    <Stack gap="xs">
      {loading
        ? <SkeletonBlock width={SKELETON.kvLabelW} height={SKELETON.kvLabelH} />
        : labelNode}

      {loading ? (
        <SkeletonBlock width={SKELETON.kvValueW} height={SKELETON.kvValueH} />
      ) : (
        // Tell a nested `<Editable>` that the label-side affordance is
        // already shown — it should keep hover background + click, but
        // skip its own value-side pencil.
        <LabelAffordanceContext.Provider value={isEditable}>
          {valueNode}
        </LabelAffordanceContext.Provider>
      )}
    </Stack>
  );

  const useFullWidth = layout === "grid" && item.fullWidth;

  return (
    <Box
      testId={toLabelSlug(item.label, idx)}
      gridSpan={useFullWidth ? "full" : undefined}
    >
      <div
        onMouseEnter={isEditable ? () => setHovered(true)  : undefined}
        onMouseLeave={isEditable ? () => setHovered(false) : undefined}
      >
        {cellContent}
      </div>
    </Box>
  );
}

/**
 * One ledger line: label and value side by side on a shared baseline, each in
 * its own hugging track so every row's columns line up. Emits TWO grid
 * children (not a wrapper) — that is what makes the tracks shared.
 */
function KVLedgerRow({
  item, idx, loading, align,
}: { item: KVItem; idx: number; loading: boolean; align: "left" | "right" }) {
  const [hovered, setHovered] = useState(false);
  const {
    lockMeta, isLocked, isEditable, renderedValue, valueTextSize, valueTone,
  } = useKVItemState(item);

  // Hover escalates the pencil, and it must escalate from EITHER half of the
  // row — the two cells are siblings in the grid, so both carry the handlers.
  const hoverProps = isEditable
    ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
    : {};

  return (
    <>
      <Box testId={toLabelSlug(item.label, idx)}>
        <div {...hoverProps}>
          {loading ? (
            <SkeletonBlock width={SKELETON.kvLabelW} height={SKELETON.kvLabelH} />
          ) : (
            withLockReason(
              <Inline as="span" inline gap="xs" align="center" justify={align === "right" ? "end" : "start"}>
                <KVAffordances isLocked={isLocked} isEditable={isEditable} hovered={hovered} />
                {/* Sentence case, not the eyebrow — a document names its fields,
                    it doesn't shout them. */}
                {/* Right-aligned (header) ledgers keep their labels on one
                    line — there are three or four of them and the block sizes
                    itself. Left-aligned ledgers sit in a shared grid track, so
                    a long label like "ID systemu zewnętrznego" held under
                    `nowrap` pushed its `max-content` column past the group's
                    share and overlapped the next one. */}
                <TextValue
                  as="span"
                  size={valueTextSize}
                  tone="muted"
                  align={align}
                  nowrap={align === "right"}
                >
                  {item.label}
                </TextValue>
              </Inline>,
              isLocked,
              lockMeta?.reason,
            )
          )}
        </div>
      </Box>

      <Box>
        <div {...hoverProps}>
          {loading ? (
            <SkeletonBlock width={SKELETON.kvValueW} height={SKELETON.kvValueH} />
          ) : (
            <LabelAffordanceContext.Provider value={isEditable}>
              <TextValue
                as="div"
                size={valueTextSize}
                weight="semibold"
                tone={valueTone}
                lineHeight="normal"
                align={align}
                // Left-aligned ledgers sit in a `1fr` track inside a
                // multi-column grid; an unbreakable value (a KSeF id, an IBAN)
                // otherwise overflows and collides with the next column.
                wrapAnywhere={align === "left"}
                testId={toValueTestId(item.label, idx)}
              >
                {renderedValue}
              </TextValue>
              {item.sub != null && (
                <TextValue as="div" size="xs" tone="muted" align={align} lineHeight="relaxed">
                  {item.sub}
                </TextValue>
              )}
            </LabelAffordanceContext.Provider>
          )}
        </div>
      </Box>
    </>
  );
}

export function KVGrid({
  items,
  layout = "grid",
  ledgerAlign = "right",
  columns = 2,
  compact = false,
  loading = false,
  testId,
}: KVGridProps) {
  const visible = items.filter((it) => !it.hidden);
  if (visible.length === 0) return null;

  if (layout === "ledger") {
    return (
      <Grid
        testId={testId}
        columns={ledgerAlign === "right" ? "label-value-tight" : "label-value"}
        justifyTracks={ledgerAlign === "right" ? "end" : undefined}
        rowGap={compact ? "xs" : "sm"}
        columnGap="md"
      >
        {visible.map((it, idx) => (
          <KVLedgerRow
            key={idx}
            item={it}
            idx={idx}
            loading={loading}
            align={ledgerAlign}
          />
        ))}
      </Grid>
    );
  }

  if (layout === "grid") {
    return (
      <Grid
        testId={testId}
        columns={compact ? 1 : columns}
        rowGap={compact ? "sm" : "md"}
        columnGap="lg"
      >
        {visible.map((it, idx) => (
          <KVCell
            key={idx}
            item={it}
            idx={idx}
            loading={loading}
            layout={layout}
          />
        ))}
      </Grid>
    );
  }

  return (
    <Stack testId={testId} gap={compact ? "sm" : "md"}>
      {visible.map((it, idx) => (
        <KVCell
          key={idx}
          item={it}
          idx={idx}
          loading={loading}
          layout={layout}
        />
      ))}
    </Stack>
  );
}
